import {Component, forwardRef, OnInit} from '@angular/core';
import {DiceLexer, Token, TokenType} from 'dice-typescript/dist/lexer';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';

@Component({
    selector: 'diceformula',
    template: `
        <ion-row>
            @for (column of columns; track column) {
                <ion-col [size]="column.size">
                    @for (button of column.buttons; track button) {
                        <ion-button class="ion-no-margin monospaced"
                                    [color]="button.color ? button.color: 'medium'"
                                    expand="full"
                                    [disabled]="button.disabled && button.disabled()"
                                    (click)="button.callback()">
                            @if (button.label) {
                                <ion-label>{{ button.label }}</ion-label>
                            }
                            @if (button.icon) {
                                <ion-icon [name]="button.icon"></ion-icon>
                            }
                        </ion-button>
                    }

                </ion-col>
            }

        </ion-row>
    `,
    styles: [`
        ion-button {
            font-size: 20px;
        }
    `],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => DiceformulaComponent),
        multi: true
    }],
    standalone: false
})
export class DiceformulaComponent implements ControlValueAccessor, OnInit {

    private tokens: Token[];

    public columns = [{
        size: 2,
        buttons: [{
            label: '7',
            callback: () => this.append(7)
        }, {
            label: '4',
            callback: () => this.append(4)
        }, {
            label: '1',
            callback: () => this.append(1)
        }, {
            label: '0',
            callback: () => this.append(0)
        }]
    }, {
        size: 2,
        buttons: [{
            label: '8',
            callback: () => this.append(8)
        }, {
            label: '5',
            callback: () => this.append(5)
        }, {
            label: '2',
            callback: () => this.append(2)
        }, {
            label: '-',
            callback: () => this.append('-')
        }]
    }, {
        size: 2,
        buttons: [{
            label: '9',
            callback: () => this.append(9)
        }, {
            label: '6',
            callback: () => this.append(6)
        }, {
            label: '3',
            callback: () => this.append(3)
        }, {
            label: '+',
            callback: () => this.append('+')
        }]
    }, {
        size: 3,
        buttons: [{
            label: 'D100',
            callback: () => this.add('100'),
            color: 'dark'
        }, {
            label: 'D12',
            callback: () => this.add('12'),
            color: 'dark'
        }, {
            label: 'D8',
            callback: () => this.add('8'),
            color: 'dark'
        }, {
            label: 'D4',
            callback: () => this.add('4'),
            color: 'dark'
        }]
    }, {
        size: 3,
        buttons: [{
            label: 'D20',
            callback: () => this.add('20'),
            color: 'dark'
        }, {
            label: 'D10',
            callback: () => this.add('10'),
            color: 'dark'
        }, {
            label: 'D6',
            callback: () => this.add('6'),
            color: 'dark'
        }, {
            icon: 'backspace',
            color: 'warning',
            disabled: () => !this.tokens || this.tokens.length === 0,
            callback: () => this.reset()
        }]
    }];

    ngOnInit(): void {

    }

    writeValue(values: string) {
        this.tokens = [];
        if (values) {
            const lexer = new DiceLexer(values);
            // look for occurrence of same dice
            let token = lexer.getNextToken();
            while (token.type !== TokenType.Terminator) {
                this.tokens.push(token);
                token = lexer.getNextToken();
            }
        }
    }

    propagateChange = (_: any) => {
    }
    propagateTouch = () => {
    }

    registerOnChange(fn) {
        this.propagateChange = fn;
    }

    registerOnTouched(fn) {
        this.propagateTouch = fn;
    }


    add(die: string) {

        let found = false;
        for (let i = 0; i < this.tokens.length; i++) {
            const token = this.tokens[i];
            if (token.type === TokenType.Identifier && this.tokens[i + 1].value === die) {
                if (this.tokens[i - 1]?.type === TokenType.Number) {
                    this.tokens[i - 1].value = (Number(this.tokens[i - 1].value) + 1) + '';
                } else {
                    this.tokens.splice(i, 0, {
                        value: '2',
                        type: TokenType.Number,
                        position: 0
                    });
                }
                found = true;
                break;
            }
        }

        if (!found) {
            if (this.tokens.length > 0 &&
                this.tokens[this.tokens.length - 1].type !== TokenType.Minus &&
                this.tokens[this.tokens.length - 1].type !== TokenType.Plus
            ) {
                this.tokens.push({
                    value: '+',
                    type: TokenType.Plus,
                    position: 0
                });
            }

            this.tokens.push({
                value: 'd',
                type: TokenType.Identifier,
                position: 0
            }, {
                value: die,
                type: TokenType.Number,
                position: 0
            });
        }

        this.patch();
    }

    append(value: any) {
        switch (value) {
            case '+':
            case '-':
                // if the last token is a '+' of '-', remove it
                if (this.tokens.length > 0 &&
                    (this.tokens[this.tokens.length - 1].type === TokenType.Minus ||
                        this.tokens[this.tokens.length - 1].type === TokenType.Plus)
                ) {
                    this.tokens.splice(this.tokens.length - 1);
                }

                this.tokens.push({
                    value: value,
                    type: value === '+' ? TokenType.Plus : TokenType.Minus,
                    position: 0
                });
                break;
            default:
                // if the last token is a die, check the second to last token if it is a dice identifier
                if (this.tokens.length >= 2 && this.tokens[this.tokens.length - 2].type === TokenType.Identifier) {
                    // start a 'new number'
                    this.tokens.push({
                        value: '+',
                        type: TokenType.Plus,
                        position: 0
                    });
                }

                this.tokens.push({
                    value: value,
                    type: TokenType.Number,
                    position: 0
                });
                break;
        }

        this.patch();
    }

    private patch(): void {
        this.propagateChange(this.tokens
            .map(token => {
                return token.value;
            })
            .filter(value => value !== null && value !== undefined)
            // .map(value => value.toLowerCase())
            .join(''));
        this.propagateTouch();
    }

    reset(): void {
        let index = this.tokens.length - 1;
        // if the last item is a dice roll, remove it
        if (this.tokens.length >= 2 && this.tokens[this.tokens.length - 2].type === TokenType.Identifier) {
            // check if the preceding token is a multiplier
            if (this.tokens.length >= 3 && this.tokens[this.tokens.length - 3].type === TokenType.Number) {
                index = this.tokens.length - 3;
            } else {
                index = this.tokens.length - 2;
            }

        } else {
            // just remove the last item
        }
        this.tokens.splice(index);

        this.patch();
    }
}
